import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Clock, List, LayoutGrid, Users, CheckCircle, Edit, Trash2, ChevronUp, ChevronDown, UserX, UtensilsCrossed, Undo2, ChevronRight, ChevronLeft, AlertCircle, MessageSquare, User, Phone, Globe, UserPlus, RotateCcw, XCircle, Ghost } from 'lucide-react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { Button } from '../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../components/ui/alert-dialog';
import { ReservationForm } from '../components/reservations/ReservationForm';
import { ReservationTimeline } from '../components/reservations/ReservationTimeline';
import { ReservationListPhoneCell } from '../components/reservations/ReservationListPhoneCell';
import { MobileReservationRow } from '../components/reservations/MobileReservationRow';
// import { SwipeableReservationRow } from '../components/reservations/SwipeableReservationRow'; // Removed unused import
import { useIsMobile } from '../hooks/useMediaQuery';
import { useCoalescedSyncRefresh } from '../hooks/useCoalescedSyncRefresh';
import { translations } from '../lib/i18n';
import { formatDate, cn } from '../lib/utils';
import { getShiftForCurrentTime, sortActiveShiftsForDisplay } from '../lib/shiftUtils';
import type { 
  Reservation, 
  Customer, 
  Table, 
  Shift, 
  ReservationViewMode, 
  ReservationTimelineData,
  CreateReservationData,
  TimeSlot
} from '../types';
import localDatabase from '../services/localDatabase';
import toast from 'react-hot-toast';
import { publishCrossTab, subscribeCrossTab } from '../services/crossTab';
import { pushPendingToSupabase } from '../services/supabaseSync';
import { checkReservationConflicts } from '../services/conflictChecker';
import { customerService } from '../services/supabaseService';
import { useTenant } from '../contexts/TenantContext';
import {
  getCustomerNotesForDisplay,
  getCustomerPhoneForDisplay,
  getReservationNotesForDisplay,
  reservationHasUserFacingNotes,
  getTelHrefForPhone,
  isWalkInSystemCustomer,
} from '../lib/customerClassification';

const NO_SHOW_OVERRIDE_STORAGE_KEY = 'reservations:no-show-overrides';

const mapReservation = (
  raw: any,
  customerList: Customer[],
  tableList: Table[],
  shiftList: Shift[]
): Reservation => ({
  ...raw,
  // Normalize IDs/dates from mixed sources (IndexedDB/Supabase) to keep filters consistent.
  shift_id: raw.shift_id != null ? String(raw.shift_id) : raw.shift_id,
  date: typeof raw.date === 'string' ? raw.date.substring(0, 10) : raw.date,
  // Normalize time to HH:MM (remove seconds if present from Supabase)
  time: raw.time ? raw.time.substring(0, 5) : raw.time,
  table_ids: Array.isArray(raw.table_ids)
    ? raw.table_ids
    : raw.table_id
      ? [raw.table_id]
      : [],
  customer: customerList.find(customer => customer.id === raw.customer_id),
  table: raw.table_id ? tableList.find(table => table.id === raw.table_id) : undefined,
  tables: Array.isArray(raw.table_ids)
    ? raw.table_ids
        .map((tableId: string) => tableList.find((table) => table.id === tableId))
        .filter((table: Table | undefined): table is Table => !!table)
    : raw.table_id
      ? tableList.filter((table) => table.id === raw.table_id)
      : [],
  shift: shiftList.find(shift => String(shift.id) === String(raw.shift_id))
})

const renderReservationTables = (reservation: Reservation) => {
  const resolvedTables = reservation.tables || [];

  if (resolvedTables.length > 1) {
    const zone = resolvedTables[0]?.zone || reservation.table?.zone;
    return (
      <div>
        <div className="font-medium text-foreground">
          Τραπέζια {resolvedTables.map((table: Table) => table.number).join(', ')}
        </div>
        {zone ? <div className="text-sm text-muted-foreground">({zone})</div> : null}
      </div>
    );
  }

  if (reservation.table) {
    return (
      <div className="flex items-center gap-1">
        <span className="font-medium text-foreground">
          Τραπέζι {reservation.table.number}
        </span>
        <span className="text-sm text-muted-foreground">
          ({reservation.table.zone})
        </span>
      </div>
    );
  }

  return (
    <span className="text-sm text-muted-foreground italic">
      Αυτόματη ανάθεση
    </span>
  );
};

// Time helper functions for no-show detection
const isReservationPast = (reservation: Reservation, gracePeriodMinutes: number = 15): boolean => {
  const now = new Date();
  const reservationDateTime = new Date(`${reservation.date}T${reservation.time}:00`);
  const gracePeriodMs = gracePeriodMinutes * 60 * 1000;
  
  return now.getTime() > (reservationDateTime.getTime() + gracePeriodMs);
};

const shouldShowNoShowButton = (reservation: Reservation): boolean => {
  // Only show for confirmed reservations that are past their time + grace
  if (reservation.status !== 'confirmed') return false;
  return isReservationPast(reservation, 15);
};

const shouldAutoMarkNoShow = (reservation: Reservation): boolean => {
  // Auto-mark as no-show only for still-confirmed, unseated reservations after grace
  if (reservation.status !== 'confirmed') return false;
  const now = new Date();
  const reservationDateTime = new Date(`${reservation.date}T${reservation.time}:00`);
  const graceMs = 15 * 60 * 1000;
  const graceDeadline = reservationDateTime.getTime() + graceMs;

  if (now.getTime() <= graceDeadline) return false;

  // If the reservation was manually touched after grace (e.g. explicit reopen),
  // treat that as an intentional manager override and skip auto no-show.
  const updatedAtMs = reservation.updated_at ? new Date(reservation.updated_at).getTime() : 0;
  if (Number.isFinite(updatedAtMs) && updatedAtMs > graceDeadline) return false;

  return true;
};

const readNoShowOverrideMap = (): Record<string, true> => {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(NO_SHOW_OVERRIDE_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const normalized: Record<string, true> = {};
    for (const id of Object.keys(parsed as Record<string, unknown>)) {
      normalized[id] = true;
    }
    return normalized;
  } catch (error) {
    console.warn('Failed to parse no-show override map:', error);
    return {};
  }
};

const writeNoShowOverrideMap = (map: Record<string, true>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(NO_SHOW_OVERRIDE_STORAGE_KEY, JSON.stringify(map));
};

const setNoShowOverride = (reservationId: string, enabled: boolean) => {
  const current = readNoShowOverrideMap();
  if (enabled) {
    current[reservationId] = true;
  } else {
    delete current[reservationId];
  }
  writeNoShowOverrideMap(current);
};

const shouldAutoComplete = (reservation: Reservation): boolean => {
  // Auto-complete if in-progress and past 2h5m from scheduled time
  if (reservation.status !== 'in-progress') return false;
  const RESERVATION_DURATION_MILLIS = (120 + 5) * 60 * 1000;
  const reservationDateTime = new Date(`${reservation.date}T${reservation.time}:00`);
  const now = new Date();
  return now.getTime() > reservationDateTime.getTime() + RESERVATION_DURATION_MILLIS;
};

// Generate timeline data from reservations

// Find shift that contains a specific time (HH:MM)
const getShiftForTime = (shifts: Shift[], time: string): string | undefined => {
  if (!time) return undefined;
  const [hour, minute] = time.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;
  const minutesTarget = hour * 60 + minute;

  for (const shift of shifts) {
    const [startHour, startMinute] = shift.start_time.split(':').map(Number);
    const [endHour, endMinute] = shift.end_time.split(':').map(Number);
    const startMinutes = startHour * 60 + startMinute;
    let endMinutes = endHour * 60 + endMinute;

    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    let target = minutesTarget;
    if (target < startMinutes) {
      target += 24 * 60;
    }

    if (target >= startMinutes && target < endMinutes) {
      return String(shift.id);
    }
  }

  return undefined;
};

const generateTimelineData = (reservations: Reservation[], date: string, sourceShifts: Shift[]): ReservationTimelineData => {
  const dayReservations = reservations.filter(r => r.date === date);
  const baseShifts = sourceShifts;

  if (baseShifts.length === 0) {
    return { date, shifts: [] };
  }

  // Collect shift IDs that actually exist, so we can assign orphaned reservations
  const knownShiftIds = new Set(baseShifts.map(s => String(s.id)));

  // Find reservations whose shift_id doesn't match any known shift (orphans)
  // and collect their times so we can assign them to the appropriate shift by time
  const orphanedReservations = dayReservations.filter(r => !knownShiftIds.has(String(r.shift_id)));

  const shifts = baseShifts.map((shift, shiftIndex) => {
    const shiftReservations = dayReservations.filter(r => String(r.shift_id) === String(shift.id));
    const [sH, sM] = shift.start_time.split(':').map(Number);
    const [eH, eM] = shift.end_time.split(':').map(Number);
    const startMinutes = sH * 60 + (sM || 0);
    let endMinutes = eH * 60 + (eM || 0);
    // Support overnight shifts (e.g. 22:00 -> 02:00)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    // Assign orphaned reservations to the shift whose time range covers the reservation's time.
    // If none covers it, assign to the first shift as a fallback so they're never invisible.
    const orphansForThisShift = orphanedReservations.filter(r => {
      const [rH, rM] = r.time.split(':').map(Number);
      const baseMinutes = rH * 60 + (rM || 0);
      // Normalize reservation minutes into shift day span for overnight safety
      const normalizedMinutes = baseMinutes < startMinutes ? baseMinutes + 24 * 60 : baseMinutes;
      const inRange = normalizedMinutes >= startMinutes && normalizedMinutes < endMinutes;
      // Assign to first shift as a last resort if nothing matched
      if (!inRange && shiftIndex === 0) {
        return !baseShifts.some(s2 => {
          const [s2H, s2M] = s2.start_time.split(':').map(Number);
          const [e2H, e2M] = s2.end_time.split(':').map(Number);
          const s2Start = s2H * 60 + (s2M || 0);
          let s2End = e2H * 60 + (e2M || 0);
          if (s2End <= s2Start) s2End += 24 * 60;
          const s2Normalized = baseMinutes < s2Start ? baseMinutes + 24 * 60 : baseMinutes;
          return s2Normalized >= s2Start && s2Normalized < s2End;
        });
      }
      return inRange;
    });

    const allReservationsForShift = [...shiftReservations, ...orphansForThisShift];
    
    const timeSlots: TimeSlot[] = [];
    // Generate slots every 30 minutes using exact shift boundaries (minute-accurate)
    for (let cursor = startMinutes; cursor < endMinutes; cursor += 30) {
      const displayMinutes = cursor % (24 * 60);
      const hour = Math.floor(displayMinutes / 60);
      const minute = displayMinutes % 60;
      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const reservationsAtTime = allReservationsForShift.filter(r => r.time === time);
      timeSlots.push({
        time,
        available: reservationsAtTime.length === 0,
        reservations: reservationsAtTime
      });
    }
    
    return {
      shift,
      timeSlots
    };
  });
  
  return {
    date,
    shifts
  };
};

const Reservations: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { tenantId, dbReady } = useTenant();
  const [viewMode, setViewMode] = useState<ReservationViewMode>('timeline');
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedShift, setSelectedShift] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'time' | 'customer' | 'party_size'>('time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState(false);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const lastKnownShiftsRef = useRef<Shift[]>([]);
  const refreshSeqRef = useRef(0);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [detailsReservation, setDetailsReservation] = useState<Reservation | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [reservationToSeat, setReservationToSeat] = useState<Reservation | null>(null);
  const [selectedSeatTableId, setSelectedSeatTableId] = useState<string>('');
  const [showListScrollRightHint, setShowListScrollRightHint] = useState(false);
  const [showListScrollLeftHint, setShowListScrollLeftHint] = useState(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  /** The actual horizontally scrollable element (mobile: overflow-x div; desktop: ScrollContainer root). */
  const listHScrollRef = useRef<HTMLDivElement | null>(null);
  const [openNewReservation, setOpenNewReservation] = useState(false);
  const [pendingNotificationTarget, setPendingNotificationTarget] = useState<{
    reservationId?: string;
    date: string;
    time?: string;
    shiftId?: string;
  } | null>(null);
  const [scrollToReservationId, setScrollToReservationId] = useState<string | null>(null);
  
  useEffect(() => {
    const state = location.state as any;

    // Open new reservation modal if requested
    if (state?.openNewReservation) {
      setOpenNewReservation(true);
      navigate(location.pathname, { replace: true, state: { ...state, openNewReservation: false } });
    }

    // Switch initial view mode when coming from other pages (e.g. /timeline header buttons)
    if (state?.viewMode === 'timeline' || state?.viewMode === 'list') {
      setViewMode(state.viewMode);
      navigate(location.pathname, { replace: true, state: { ...state, viewMode: undefined } });
    }
    
    if (state?.fromNotification && state.targetDate) {
      setPendingNotificationTarget({
        reservationId: state.targetReservationId,
        date: state.targetDate,
        time: state.targetTime,
        shiftId: state.targetShiftId,
      });
      // Clear navigation state so back button behaves normally
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // When we have a pending target and shifts loaded, jump to that date & shift
  useEffect(() => {
    if (!pendingNotificationTarget) return;
    if (shifts.length === 0) return; // wait until shifts are loaded
    if (reservations.length === 0) return; // wait until reservations are loaded

    const { reservationId, date, time, shiftId } = pendingNotificationTarget;
    setSelectedDate(date);

    // 1) If we know the exact reservation, trust its shift_id
    if (reservationId) {
      const targetReservation = reservations.find((r) => r.id === reservationId);
      if (targetReservation && targetReservation.shift_id) {
        setSelectedShift(String(targetReservation.shift_id));
        setScrollToReservationId(reservationId);
        setPendingNotificationTarget(null);
        return;
      }
    }

    // 2) Prefer explicit shiftId from navigation state if valid
    if (shiftId && shifts.some((s) => String(s.id) === String(shiftId))) {
      setSelectedShift(String(shiftId));
    } else if (time) {
      // 3) Fallback: infer shift from time window
      const normalizedTime = time.slice(0, 5);
      const targetShiftId = getShiftForTime(shifts, normalizedTime);
      if (targetShiftId) {
        setSelectedShift(targetShiftId);
      }
    }

    // Set scroll target even if shift was determined via fallback
    if (reservationId) {
      setScrollToReservationId(reservationId);
    }

    setPendingNotificationTarget(null);
  }, [pendingNotificationTarget, shifts, reservations]);

  // Auto-scroll to target reservation after navigation from notifications
  useEffect(() => {
    if (!scrollToReservationId) return;

    // Retry mechanism to find the element (may take time to render)
    let attempts = 0;
    const maxAttempts = 10;
    const retryDelay = 200;

    // Apple-style smooth scroll with custom easing
    const smoothScrollTo = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const targetY = absoluteTop - (window.innerHeight / 2) + (rect.height / 2);
      
      // Find the scrollable container
      const scrollContainer = element.closest('[data-radix-scroll-area-viewport], .overflow-y-auto, .overflow-auto') as HTMLElement | null;
      
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRelativeTop = rect.top - containerRect.top + scrollContainer.scrollTop;
        const scrollTarget = elementRelativeTop - (scrollContainer.clientHeight / 2) + (rect.height / 2);
        
        // Smooth scroll with native behavior
        scrollContainer.scrollTo({
          top: scrollTarget,
          behavior: 'smooth'
        });
      } else {
        // Fallback to window scroll
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      }
    };

    const tryScroll = () => {
      // Look for either list row or timeline card
      const element = 
        document.getElementById(`reservation-row-${scrollToReservationId}`) ||
        document.getElementById(`reservation-card-${scrollToReservationId}`);
      
      if (element) {
        // Perform smooth scroll
        smoothScrollTo(element);

        // Add attention animation after scroll settles
        setTimeout(() => {
          element.classList.add('reservation-attention');
          
          // Remove animation class after it completes
          setTimeout(() => {
            element.classList.remove('reservation-attention');
          }, 2100);
        }, 600);
        
        setScrollToReservationId(null);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, retryDelay);
      } else {
        // Give up after max attempts
        setScrollToReservationId(null);
      }
    };

    // Initial delay to allow data loading
    const timeoutId = setTimeout(tryScroll, 400);

    return () => clearTimeout(timeoutId);
  }, [scrollToReservationId]);
  
  const timelineData = generateTimelineData(reservations, selectedDate, shifts);

  const activeShiftsOrdered = useMemo(
    () => sortActiveShiftsForDisplay(shifts),
    [shifts],
  );

  const refreshData = useCallback(async () => {
    // Guard: ensure database is initialized before attempting any operations
    if (!dbReady || !localDatabase.isInitialized()) {
      console.warn('Skipping refreshData: database not ready yet');
      return;
    }

    const seq = ++refreshSeqRef.current;

    try {
      const [allCustomers, allTables, allShifts, allReservations] = await Promise.all([
        localDatabase.getAllCustomers(),
        localDatabase.getAllTables(),
        localDatabase.getAllShifts(),
        localDatabase.getAllReservations()
      ]);

      if (seq !== refreshSeqRef.current) return;

      const statusMap = (status: string | undefined) => {
        if (!status) return 'available'
        if (status === 'active') return 'available'
        if (status === 'inactive') return 'reserved'
        return status as any
      }

      const normalizedTables = (allTables as any[]).map(table => ({
        ...table,
        status: statusMap(table.status)
      })) as Table[]
      const normalizedShifts = (allShifts as any[]).map((shift) => ({
        ...shift,
        id: String(shift.id),
      })) as Shift[]
      const availableShifts = normalizedShifts.length
        ? normalizedShifts
        : lastKnownShiftsRef.current
      if (normalizedShifts.length) {
        lastKnownShiftsRef.current = normalizedShifts
      }

      setCustomers(
        (allCustomers as unknown as Customer[]).filter((c) => !isWalkInSystemCustomer(c)),
      )
      setTables(normalizedTables)
      setShifts(availableShifts)
      setReservations(
        (allReservations as any[]).map(raw =>
          mapReservation(raw, allCustomers as unknown as Customer[], normalizedTables, availableShifts)
        )
      )

      if (availableShifts.length > 0) {
        const currentShiftId = getShiftForCurrentTime(availableShifts);
        setSelectedShift((prev) => {
          if (prev && availableShifts.some((shift) => String(shift.id) === String(prev))) {
            return prev;
          }
          return String(currentShiftId ?? availableShifts[0].id);
        });
      } else {
        setSelectedShift('')
      }
    } catch (error) {
      if (seq !== refreshSeqRef.current) return;
      console.error('Failed to load reservation data:', (error as Error).message)
    }
  }, [dbReady])

  const scheduleCrossTabRefresh = useCoalescedSyncRefresh(() => {
    void refreshData();
  });

  const resolveCustomerId = useCallback(async (newCustomer: { name: string; phone: string; notes?: string }) => {
    if (!tenantId) throw new Error('Δεν έχει επιλεγεί επιχείρηση (tenant).');
    const phone = newCustomer.phone?.trim();
    if (!phone) {
      throw new Error('Απαιτείται τηλέφωνο πελάτη');
    }

    // 1) Local match by phone
    const existingLocal = await localDatabase.getCustomerByPhone(phone);
    if (existingLocal) {
      const updates: Partial<Customer> = {};
      if (newCustomer.name && newCustomer.name !== existingLocal.name) {
        updates.name = newCustomer.name;
      }
      if ((newCustomer.notes ?? '') !== (existingLocal.notes ?? '')) {
        updates.notes = newCustomer.notes ?? '';
      }
      if (Object.keys(updates).length) {
        await localDatabase.updateCustomer(existingLocal.id, updates as any);
      }
      return existingLocal.id;
    }

    // 2) Remote match by phone (if online) - pull it locally to keep IDs aligned
    if (navigator.onLine) {
      try {
        const existingRemote = await customerService.getCustomerByPhone(phone, tenantId);
        if (existingRemote) {
          await localDatabase.bulkUpsertCustomers([existingRemote as any]);
          return existingRemote.id;
        }
      } catch (error) {
        console.warn('Αποτυχία αναζήτησης πελάτη στο Supabase:', error);
      }
    }

    // 3) Create new local record (will sync later)
    return await localDatabase.createCustomer({
      tenant_id: tenantId,
      name: newCustomer.name,
      phone,
      notes: newCustomer.notes || ''
    });
  }, []);

  useEffect(() => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    refreshData();
  }, [refreshData, tenantId, dbReady]);

  useEffect(() => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    
    const unsubscribe = subscribeCrossTab((message) => {
      if (
        message.type === 'sync:reservations' ||
        message.type === 'sync:tables' ||
        message.type === 'sync:shifts' ||
        message.type === 'sync:customers' ||
        message.type === 'sync:all'
      ) {
        scheduleCrossTabRefresh();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [scheduleCrossTabRefresh, tenantId, dbReady]);

  useEffect(() => {
    if (shifts.length === 0) return;
    if (!selectedShift || !shifts.some((shift) => String(shift.id) === String(selectedShift))) {
      setSelectedShift(String(shifts[0].id));
    }
  }, [shifts, selectedShift]);

  // Automatic no-show detection (silent; only warns on failure)
  useEffect(() => {
    const checkForNoShows = async () => {
      const noShowOverrides = readNoShowOverrideMap();
      const activeReservationIds = new Set(reservations.map((r) => r.id));
      const staleOverrideIds = Object.keys(noShowOverrides).filter((id) => !activeReservationIds.has(id));
      if (staleOverrideIds.length > 0) {
        for (const id of staleOverrideIds) {
          delete noShowOverrides[id];
        }
        writeNoShowOverrideMap(noShowOverrides);
      }

      const targets = reservations
        .filter((r) => shouldAutoMarkNoShow(r) && !noShowOverrides[r.id])
        .map((r) => r.id);
      if (!targets.length) return;
      for (const id of targets) {
        try {
          await localDatabase.updateReservation(id, { status: 'no-show' } as any);
        } catch (err) {
          console.warn('Auto no-show update failed for', id, err);
          }
      }
      if (targets.length) {
        await refreshData();
        publishCrossTab({ type: 'sync:reservations' });
        if (navigator.onLine) {
          pushPendingToSupabase().catch(err => console.warn('Push pending failed (auto no-show):', err));
        }
      }
    };

    // Check immediately on mount
    checkForNoShows();

    // Check every 5 minutes for auto no-shows
    const interval = setInterval(checkForNoShows, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [reservations, refreshData]);

  // Automatic completion for in-progress after duration
  useEffect(() => {
    const checkForCompletion = async () => {
      const toComplete = reservations.filter(r => shouldAutoComplete(r)).map(r => r.id);
      if (!toComplete.length) return;
      for (const id of toComplete) {
        try {
          await localDatabase.updateReservation(id, { status: 'completed', table_id: null } as any);
        } catch (err) {
          console.warn('Auto-complete failed for', id, err);
        }
      }
      if (toComplete.length) {
        await refreshData();
        publishCrossTab({ type: 'sync:reservations' });
        if (navigator.onLine) {
          pushPendingToSupabase().catch(err => console.warn('Push pending failed (auto complete):', err));
        }
      }
    };

    // Run immediately and then every 5 minutes
    checkForCompletion();
    const interval = setInterval(checkForCompletion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [reservations, refreshData]);

  // Helper function to filter and sort reservations for list view
  const getFilteredReservations = () => {
    const knownShiftIds = new Set(shifts.map(s => String(s.id)));
    const filtered = reservations.filter(r => {
      // Date filter
      if (r.date !== selectedDate) return false;
      // Shift filter: skip shift filtering for reservations with an unrecognised shift_id
      // so they are never silently dropped from view
      const isOrphaned = !knownShiftIds.has(String(r.shift_id));
      const matchesDateAndShift =
        isOrphaned ||
        !selectedShift ||
        String(r.shift_id) === String(selectedShift);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      
      return matchesDateAndShift && matchesStatus;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'time':
          comparison = a.time.localeCompare(b.time);
          break;
        case 'customer':
          const nameA = a.customer?.name || 'Άγνωστος πελάτης';
          const nameB = b.customer?.name || 'Άγνωστος πελάτης';
          comparison = nameA.localeCompare(nameB, 'el');
          break;
        case 'party_size':
          comparison = a.party_size - b.party_size;
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Handle column header clicks for sorting
  const handleSort = (field: 'time' | 'customer' | 'party_size') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getListHorizontalScrollEl = (): HTMLElement | null => {
    if (listHScrollRef.current) return listHScrollRef.current;
    const wrapper = listScrollRef.current;
    if (!wrapper) return null;
    const containers = wrapper.getElementsByTagName('div');
    for (let i = 0; i < containers.length; i++) {
      const el = containers[i];
      if (el.scrollWidth > el.clientWidth || el.style.overflow?.includes('auto') || el.style.overflow?.includes('scroll')) {
        return el;
      }
    }
    return null;
  };

  const updateScrollHints = () => {
    const scrollableEl = getListHorizontalScrollEl();
    if (!scrollableEl) return;
    
    const maxScroll = Math.max(scrollableEl.scrollWidth - scrollableEl.clientWidth, 0);
    const atStart = scrollableEl.scrollLeft < 4;
    const hasOverflow = maxScroll > 8;
    setShowListScrollRightHint(hasOverflow && atStart);
    setShowListScrollLeftHint(hasOverflow && !atStart);
  };

  const handleListScroll = () => {
    updateScrollHints();
  };

  // Disabled horizontal wheel scrolling - users can use buttons or click-and-drag instead
  // Vertical scrolling works normally for the page

  const scrollListToActions = () => {
    const wrapper = listScrollRef.current;
    if (!wrapper) return;
    
    // ScrollContainer creates a scrollable child div - find it
    const containers = wrapper.getElementsByTagName('div');
    let scrollableEl: HTMLElement | null = null;
    
    for (let i = 0; i < containers.length; i++) {
      const el = containers[i];
      if (el.scrollWidth > el.clientWidth || el.style.overflow?.includes('auto') || el.style.overflow?.includes('scroll')) {
        scrollableEl = el;
        break;
      }
    }
    
    if (!scrollableEl) return;
    
    const target = scrollableEl.scrollWidth - scrollableEl.clientWidth;
    scrollableEl.scrollTo({ left: target, behavior: 'smooth' });
    setShowListScrollRightHint(false);
    setShowListScrollLeftHint(true);
  };

  const scrollListToStart = () => {
    const scrollableEl = getListHorizontalScrollEl();
    if (!scrollableEl) return;
    
    scrollableEl.scrollTo({ left: 0, behavior: 'smooth' });
    setShowListScrollLeftHint(false);
    setShowListScrollRightHint(true);
  };

  // Detect horizontal overflow so scroll hint arrows work on all breakpoints (incl. mobile list).
  useEffect(() => {
    if (viewMode !== 'list') {
      setShowListScrollRightHint(false);
      setShowListScrollLeftHint(false);
      return;
    }

    const checkOverflow = () => {
      const scrollableEl = getListHorizontalScrollEl();
      if (!scrollableEl) {
        setShowListScrollRightHint(false);
        setShowListScrollLeftHint(false);
        return;
      }
      const maxScroll = Math.max(scrollableEl.scrollWidth - scrollableEl.clientWidth, 0);
      const hasOverflow = maxScroll > 8;
      const atStart = scrollableEl.scrollLeft < 4;
      setShowListScrollRightHint(hasOverflow && atStart);
      setShowListScrollLeftHint(hasOverflow && !atStart);
    };

    const timer = setTimeout(() => {
      checkOverflow();
    }, 100);

    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [viewMode, reservations, selectedShift, statusFilter, sortField, sortDirection, isMobile]);

  const getAvailableTablesForReservation = useCallback((reservation: Reservation): Table[] => {
    const RESERVATION_MINUTES = 120 + 5;
    const [resHour, resMinute] = reservation.time.split(':').map(Number);
    const reservationStart = resHour * 60 + resMinute;
    const reservationEnd = reservationStart + RESERVATION_MINUTES;

    return tables
      .filter(table => {
        if (!table) return false;
        if (typeof table.seats === 'number' && table.seats < reservation.party_size) return false;
        if (table.status && table.status !== 'available') return false;

        const hasConflict = reservations.some(other => {
          if (other.id === reservation.id) return false;
          if (other.status === 'cancelled') return false;
          if (!other.table_id || other.table_id !== table.id) return false;
          if (other.date !== reservation.date) return false;

          // Compare time overlap
          const [otherHour, otherMinute] = other.time.split(':').map(Number);
          const otherStart = otherHour * 60 + otherMinute;
          const otherEnd = otherStart + RESERVATION_MINUTES;

          return reservationStart < otherEnd && reservationEnd > otherStart;
        });

        return !hasConflict;
      })
      .sort((a, b) => {
        const numberA = typeof a.number === 'number' ? a.number : parseInt(String(a.number), 10);
        const numberB = typeof b.number === 'number' ? b.number : parseInt(String(b.number), 10);
        return numberA - numberB;
      });
  }, [tables, reservations]);

  const seatOptions = useMemo(() => {
    if (!reservationToSeat) return [];
    return getAvailableTablesForReservation(reservationToSeat);
  }, [reservationToSeat, getAvailableTablesForReservation]);

  useEffect(() => {
    if (!reservationToSeat) {
      setSelectedSeatTableId('');
      return;
    }

    if (seatOptions.length === 0) {
      setSelectedSeatTableId('');
      return;
    }

    setSelectedSeatTableId(prev => {
      if (prev && seatOptions.some(table => table.id === prev)) {
        return prev;
      }
      return seatOptions[0].id;
    });
  }, [reservationToSeat, seatOptions]);

  const normalizeLateStatusAndTable = (date: string, time: string, baseStatus: Reservation['status'], tableId?: string | null) => {
    // Determine if the slot is already past and by how much
    const slot = new Date(`${date}T${time}:00`);
    const now = new Date();
    const elapsedMs = now.getTime() - slot.getTime();
    const DURATION_MS = (120 + 5) * 60 * 1000; // 2h + 5m

    // If slot not passed, keep as-is
    if (elapsedMs <= 0) {
      return { status: baseStatus, table_id: tableId ?? undefined };
    }

    // If passed but within duration → in-progress
    if (elapsedMs > 0 && elapsedMs <= DURATION_MS) {
      return { status: 'in-progress' as const, table_id: tableId ?? undefined };
    }

    // If passed beyond duration → completed, and free table to avoid conflicts
    return { status: 'completed' as const, table_id: null };
  };

  const handleCreateReservation = async (data: CreateReservationData) => {
    if (!tenantId) {
      toast.error('Δεν έχει επιλεγεί επιχείρηση. Παρακαλώ συνδεθείτε ξανά.');
      return;
    }
    setIsLoading(true);
    try {
      // First, check for conflicts (both local and Supabase if online)
      const conflictCheck = await checkReservationConflicts({
        tenantId: tenantId!,
        date: data.date,
        time: data.time,
        shiftId: data.shift_id!,
        tableId: data.table_id || undefined,
        partySize: data.party_size
      });

      if (conflictCheck.hasConflict) {
        toast.error(conflictCheck.message || 'Υπάρχει σύγκρουση με άλλη κράτηση');
        setIsLoading(false);
        return;
      }

      // Show warning if Supabase check failed (offline or error)
      if (conflictCheck.conflictType === 'supabase_error') {
        toast(conflictCheck.message || 'Η κράτηση θα επιβεβαιωθεί κατά τον συγχρονισμό', {
          icon: '⚠️',
          duration: 5000
        });
      }

      let customerId = data.customer_id;
      if (customerId === 'new_customer' && data.newCustomer) {
        customerId = await resolveCustomerId(data.newCustomer);
        publishCrossTab({ type: 'sync:customers' });
        const updatedCustomers = await localDatabase.getAllCustomers();
        setCustomers(
          (updatedCustomers as unknown as Customer[]).filter(
            (c) => !isWalkInSystemCustomer(c),
          ),
        );
      }

      const normalized = normalizeLateStatusAndTable(
        data.date,
        data.time,
        'confirmed',
        data.table_id || undefined
      );

      await localDatabase.createReservation({
        customer_id: customerId,
        table_id: normalized.table_id,
        shift_id: data.shift_id!,
        date: data.date,
        time: data.time,
        party_size: data.party_size,
        status: normalized.status,
        notes: data.notes || ''
      } as any);

      toast.success('Η κράτηση δημιουργήθηκε επιτυχώς');
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });

      // Set these AFTER refreshData so they are not overwritten by refreshData's own setSelectedShift call
      setSelectedDate(data.date);
      if (data.shift_id) {
        setSelectedShift(String(data.shift_id));
      }
      setStatusFilter('all');
      
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (create):', err));
      }

    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error('Σφάλμα κατά τη δημιουργία της κράτησης');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditReservation = (reservation: Reservation) => {
    setEditingReservation(reservation);
  };

  const handleDeleteReservation = (reservationId: string) => {
    setShowDeleteConfirm(reservationId);
  };

  const confirmDeleteReservation = async () => {
    if (!showDeleteConfirm) return;
    const reservationId = showDeleteConfirm;
    // Close dialog immediately
    setShowDeleteConfirm(null);
    
    try {
      setNoShowOverride(reservationId, false);
      // Mark as cancelled and free up the table (soft delete)
      await localDatabase.updateReservation(reservationId, { 
        status: 'cancelled',
        table_id: null 
      } as any);
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      toast.success('Η κράτηση ακυρώθηκε και το τραπέζι αποδεσμεύτηκε');
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (cancel):', err));
      }
    } catch (error) {
      console.error('Error cancelling reservation:', (error as Error).message);
      toast.error('Σφάλμα κατά την ακύρωση της κράτησης');
    }
  };

  const openSeatDialog = (reservation: Reservation) => {
    setSelectedSeatTableId('');
    setReservationToSeat(reservation);
  };

  const closeSeatDialog = () => {
    setReservationToSeat(null);
    setSelectedSeatTableId('');
  };

  const handleSeatReservationSubmit = async () => {
    if (!tenantId) {
      toast.error('Δεν έχει επιλεγεί επιχείρηση. Παρακαλώ συνδεθείτε ξανά.');
      return;
    }
    if (!reservationToSeat || !selectedSeatTableId) {
      toast.error('Επιλέξτε τραπέζι για να συνεχίσετε.');
      return;
    }

    try {
      // Check for conflicts before seating
      const conflictCheck = await checkReservationConflicts({
        tenantId: tenantId!,
        date: reservationToSeat.date,
        time: reservationToSeat.time,
        shiftId: reservationToSeat.shift_id,
        tableId: selectedSeatTableId,
        excludeId: reservationToSeat.id // Exclude current reservation
      });

      if (conflictCheck.hasConflict) {
        toast.error(conflictCheck.message || 'Το τραπέζι είναι ήδη κατειλημμένο');
        return;
      }

      await localDatabase.updateReservation(reservationToSeat.id, { table_id: selectedSeatTableId } as any);
      toast.success('Η κράτηση ανατέθηκε στο τραπέζι.');
      closeSeatDialog();
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (seat):', err));
      }
    } catch (error) {
      console.error('Error seating reservation:', (error as Error).message);
      toast.error('Η ανάθεση τραπεζιού απέτυχε. Προσπαθήστε ξανά.');
    }
  };

  const handleUnassignTable = async (reservation: Reservation) => {
    try {
      await localDatabase.updateReservation(reservation.id, { table_id: null } as any);
      toast.success('Το τραπέζι αποδεσμεύτηκε.');
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (unassign):', err));
      }
    } catch (error) {
      console.error('Error unassigning table:', (error as Error).message);
      toast.error('Η αποδέσμευση τραπεζιού απέτυχε. Προσπαθήστε ξανά.');
    }
  };

  // Check-in: mark seated/in-progress
  const handleCheckInReservation = async (reservationId: string) => {
    try {
      setNoShowOverride(reservationId, false);
      await localDatabase.updateReservation(reservationId, { status: 'in-progress' } as any);
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (check-in):', err));
      }
    } catch (error) {
      console.error('Error checking in reservation:', (error as Error).message);
    }
  };

  // Checkout: free the table (clear table_id) while keeping status completed
  const handleCheckoutReservation = async (reservationId: string) => {
    try {
      setNoShowOverride(reservationId, false);
      await localDatabase.updateReservation(reservationId, { status: 'completed', table_id: null } as any);
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (checkout):', err));
      }
    } catch (error) {
      console.error('Error completing reservation:', (error as Error).message);
    }
  };

  const handleMarkNoShow = async (reservationId: string) => {
    try {
      setNoShowOverride(reservationId, false);
      await localDatabase.updateReservation(reservationId, { status: 'no-show' } as any);
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (no-show):', err));
      }
    } catch (error) {
      console.error('Error marking reservation as no-show:', (error as Error).message);
    }
  };

  const handleReopenReservation = async (reservationId: string) => {
    try {
      // Reopen is an explicit manager action, so it overrides the auto no-show policy.
      setNoShowOverride(reservationId, true);
      await localDatabase.updateReservation(reservationId, { status: 'confirmed' } as any);
      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (reopen):', err));
      }
      toast.success('Η κράτηση επαναφέρθηκε');
    } catch (error) {
      console.error('Error reopening reservation:', (error as Error).message);
      toast.error('Σφάλμα κατά την επαναφορά');
    }
  };

  const handleUpdateReservation = async (data: CreateReservationData) => {
    if (!editingReservation) return;
    
    setIsLoading(true);
    try {
      let customerId = data.customer_id;
      if (customerId === 'new_customer' && data.newCustomer) {
        customerId = await resolveCustomerId(data.newCustomer);
        publishCrossTab({ type: 'sync:customers' });
        const updatedCustomers = await localDatabase.getAllCustomers();
        setCustomers(
          (updatedCustomers as unknown as Customer[]).filter(
            (c) => !isWalkInSystemCustomer(c),
          ),
        );
      }

      const normalized = normalizeLateStatusAndTable(
        data.date,
        data.time,
        editingReservation.status,
        data.table_id === 'auto' ? undefined : data.table_id || undefined
      );

      await localDatabase.updateReservation(editingReservation.id, {
        customer_id: customerId,
        table_id: normalized.table_id,
        shift_id: data.shift_id!,
        date: data.date,
        time: data.time,
        party_size: data.party_size,
        status: normalized.status,
        notes: data.notes || ''
      } as any);

      await refreshData();
      publishCrossTab({ type: 'sync:reservations' });

      // Set these AFTER refreshData so they are not overwritten by refreshData's own setSelectedShift call
      setSelectedDate(data.date);
      if (data.shift_id) {
        setSelectedShift(String(data.shift_id));
      }
      setStatusFilter('all');
      if (navigator.onLine) {
        pushPendingToSupabase().catch(err => console.warn('Push pending failed (update):', err));
      }
      setEditingReservation(null);
    } catch (error) {
      console.error('Error updating reservation:', (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Desktop/tablet: Click anywhere on row to edit, except on action buttons/controls
  const handleRowClick = (reservation: Reservation, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target) return;
    // Ignore clicks on interactive elements inside the row
    if (target.closest('button, a, input, [role="button"], [contenteditable="true"]')) return;
    // Open details modal on all devices
    setDetailsReservation(reservation);
  };

  const contextReservations = reservations.filter(r =>
    r.date === selectedDate &&
    (!selectedShift || String(r.shift_id) === String(selectedShift))
  );
  const stats = {
    total: contextReservations.length,
    confirmed: contextReservations.filter(r => r.status === 'confirmed').length,
    inProgress: contextReservations.filter(r => r.status === 'in-progress').length,
    totalGuests: contextReservations
      .filter(r => r.status !== 'cancelled' && r.status !== 'no-show')
      .reduce((sum, r) => sum + r.party_size, 0),
  };

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-2.5 md:gap-3 min-w-0">
          <Calendar className="w-5 h-5 md:w-8 md:h-8 text-blue-500 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg md:text-3xl font-bold text-foreground truncate">{translations.reservations.title}</h1>
            <p className="text-sm md:text-base text-muted-foreground hidden md:block">Διαχείριση κρατήσεων και οργάνωση των τραπεζιών</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <ReservationForm
            customers={customers}
            tables={tables}
            shifts={shifts}
            reservations={reservations}
            onSubmit={handleCreateReservation}
            isLoading={isLoading}
          initialOpen={openNewReservation}
          />
        </div>
      </div>

      {/* Stats Strip — scoped to selected date + shift */}
      <div className="grid grid-cols-4 gap-2 md:gap-4">
        <div className="bg-card/95 p-2.5 md:p-4 rounded-xl border border-border/80 shadow-sm">
          <p className="text-[10px] md:text-sm font-medium text-muted-foreground leading-tight truncate">Σύνολο</p>
          <p className="text-xl md:text-2xl font-bold text-card-foreground leading-tight mt-1">{stats.total}</p>
        </div>
        <div className="bg-card/95 p-2.5 md:p-4 rounded-xl border border-border/80 shadow-sm">
          <p className="text-[10px] md:text-sm font-medium text-muted-foreground leading-tight truncate">Επιβ.</p>
          <p className="text-xl md:text-2xl font-bold text-blue-600 leading-tight mt-1">{stats.confirmed}</p>
        </div>
        <div className="bg-card/95 p-2.5 md:p-4 rounded-xl border border-border/80 shadow-sm">
          <p className="text-[10px] md:text-sm font-medium text-muted-foreground leading-tight truncate">Εξέλ.</p>
          <p className="text-xl md:text-2xl font-bold text-amber-600 leading-tight mt-1">{stats.inProgress}</p>
        </div>
        <div className="bg-card/95 p-2.5 md:p-4 rounded-xl border border-border/80 shadow-sm">
          <p className="text-[10px] md:text-sm font-medium text-muted-foreground leading-tight truncate">Άτομα</p>
          <p className="text-xl md:text-2xl font-bold text-purple-600 leading-tight mt-1">{stats.totalGuests}</p>
        </div>
      </div>

      {/* Controls — compact mobile toolbar / full desktop bar */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        {/* Mobile layout: Row 1 date + shift, Row 2 view toggle */}
        <div className="flex flex-col md:hidden">
          <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
            {/* Date input */}
            <div
              className="flex-1 cursor-pointer"
              onClick={() => {
                const input = document.getElementById('date') as HTMLInputElement;
                if (input) {
                  try { input.showPicker?.(); } catch { /* ignore */ }
                }
              }}
            >
              <input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-border rounded-lg text-sm outline-none ring-0 bg-background text-foreground dark:bg-background dark:text-foreground dark:border-border"
              />
            </div>
            {/* Shift select */}
            <div className="flex-1 min-w-0">
              <Select value={selectedShift} onValueChange={setSelectedShift}>
                <SelectTrigger id="shift-select" className="w-full h-9 text-sm rounded-lg">
                  <SelectValue placeholder="Βάρδια" />
                </SelectTrigger>
                <SelectContent>
                  {activeShiftsOrdered.map((shift) => (
                    <SelectItem key={shift.id} value={String(shift.id)}>
                      {shift.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* View toggle row */}
          <div className="mx-3 mb-3 rounded-xl bg-muted/60 p-1 border border-border/60 grid grid-cols-2">
            <button
              type="button"
              onClick={() => setViewMode('timeline')}
              className={cn(
                'flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-[background-color,color,transform] duration-150 active:scale-[0.98]',
                viewMode === 'timeline'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              Timeline
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-[background-color,color,transform] duration-150 active:scale-[0.98]',
                viewMode === 'list'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <List className="h-4 w-4" />
              Λίστα
            </button>
          </div>
        </div>

        {/* Desktop layout: single row */}
        <div className="hidden md:flex md:items-center md:justify-between md:flex-wrap gap-3 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label htmlFor="date-desktop" className="text-sm font-medium text-foreground whitespace-nowrap">
                Ημερομηνία:
              </label>
              <div
                className="relative cursor-pointer transition-[opacity,transform] duration-150 hover:opacity-90 active:scale-[0.98]"
                onClick={() => {
                  const input = document.getElementById('date-desktop') as HTMLInputElement;
                  if (input) {
                    try { input.showPicker?.(); } catch { /* ignore */ }
                  }
                }}
              >
                <input
                  id="date-desktop"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border border-border rounded-md text-sm outline-none ring-0 touch-target bg-background text-foreground dark:bg-background dark:text-foreground dark:border-border"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground whitespace-nowrap">Βάρδια:</label>
              <div role="group" aria-label="Βάρδια" className="flex flex-wrap items-center gap-2">
                {activeShiftsOrdered.map((shift) => {
                  const id = String(shift.id);
                  const isSelected = selectedShift === id;
                  return (
                    <Button
                      key={shift.id}
                      type="button"
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      aria-pressed={isSelected}
                      onClick={() => setSelectedShift(id)}
                      className={cn(
                        'touch-target shrink-0 transition-[opacity,transform] duration-200',
                        isSelected && 'shadow-sm ring-2 ring-primary/35 ring-offset-2 ring-offset-background',
                        !isSelected && 'hover:bg-accent hover:text-accent-foreground hover:border-primary/40 hover:shadow-sm active:scale-[0.98]'
                      )}
                    >
                      {shift.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={viewMode === 'timeline' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('timeline')} className="gap-2 touch-target">
              <LayoutGrid className="h-4 w-4" />
              Timeline
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')} className="gap-2 touch-target">
              <List className="h-4 w-4" />
              Λίστα
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'timeline' ? (
        /* Expanded timeline view - ~90% of available content area */
        <div className="-mx-4 md:mx-0 xl:-mx-[calc(40vw-50%)] xl:w-[80vw] xl:bg-card xl:border xl:rounded-lg xl:shadow-sm lg:-mx-8 lg:px-8 xl:px-8 2xl:px-12 bg-card rounded-lg border border-border shadow-sm">
          <div className="py-4 px-0 sm:px-2">
            <ReservationTimeline
              timelineData={timelineData}
              onEditReservation={handleEditReservation}
              onDeleteReservation={handleDeleteReservation}
              onCheckInReservation={handleCheckInReservation}
              onCompleteReservation={handleCheckoutReservation}
              onReopenReservation={handleReopenReservation}
              onMarkNoShow={handleMarkNoShow}
              selectedShift={selectedShift}
              onDateChange={setSelectedDate}
            />
          </div>
        </div>
      ) : (
        <div className="-mx-4 md:mx-0 xl:-mx-[calc(40vw-50%)] xl:w-[80vw] xl:bg-card xl:border xl:rounded-lg xl:shadow-sm lg:-mx-8 lg:px-8 xl:px-8 2xl:px-12 bg-card rounded-lg border border-border shadow-sm">
          <div className="space-y-3 md:space-y-4">
            {/* List View Header */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-muted/60 border border-border/60 rounded-xl">
              <div className="flex items-center gap-1.5 min-w-0">
                <List className="h-4 w-4 text-blue-500 shrink-0" />
                <h3 className="text-sm font-semibold text-accent-foreground">Λίστα</h3>
                <span className="text-xs text-muted-foreground shrink-0">
                  ({getFilteredReservations().length})
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Select
                  value={`${sortField}-${sortDirection}`}
                  onValueChange={(value) => {
                    const [field, direction] = value.split('-') as ['time' | 'customer' | 'party_size', 'asc' | 'desc'];
                    setSortField(field);
                    setSortDirection(direction);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs w-24 md:w-36 shrink-0 rounded-lg">
                    <SelectValue placeholder="Ώρα" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="time-asc">Ώρα ↑</SelectItem>
                    <SelectItem value="time-desc">Ώρα ↓</SelectItem>
                    <SelectItem value="customer-asc">Όνομα Α-Ω</SelectItem>
                    <SelectItem value="customer-desc">Όνομα Ω-Α</SelectItem>
                    <SelectItem value="party_size-asc">Άτομα ↑</SelectItem>
                    <SelectItem value="party_size-desc">Άτομα ↓</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-24 md:w-40 shrink-0 rounded-lg">
                    <SelectValue placeholder="Όλες" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες</SelectItem>
                    <SelectItem value="confirmed">Επιβεβαιωμένες</SelectItem>
                    <SelectItem value="in-progress">Σε εξέλιξη</SelectItem>
                    <SelectItem value="completed">Ολοκληρωμένες</SelectItem>
                    <SelectItem value="cancelled">Ακυρωμένες</SelectItem>
                    <SelectItem value="no-show">Δεν εμφανίστηκε</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Reservations List - Unified (desktop table on all devices) */}
            {/* Desktop: Traditional Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden relative">
                <div ref={listScrollRef} style={{ overscrollBehaviorY: 'auto' }}>
            {isMobile ? (
              <div className="divide-y divide-border">
                {getFilteredReservations().map((reservation) => (
                  <MobileReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    onTap={(r) => setDetailsReservation(r)}
                    onCheckIn={handleCheckInReservation}
                    onComplete={handleCheckoutReservation}
                  />
                ))}
                {getFilteredReservations().length === 0 && (
                  <div className="text-center py-12">
                    <List className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">Δεν υπάρχουν κρατήσεις</h3>
                    <p className="text-muted-foreground">
                      {statusFilter === 'all'
                        ? "Δεν βρέθηκαν κρατήσεις για την επιλεγμένη ημερομηνία και βάρδια."
                        : "Δεν βρέθηκαν κρατήσεις με την επιλεγμένη κατάσταση."
                      }
                    </p>
                  </div>
                )}
              </div>
            ) : (
                  <ScrollContainer
                    innerRef={listHScrollRef}
                    onScroll={handleListScroll}
                    onStartScroll={() => {
                      // Force check scroll state when drag starts
                      setTimeout(() => {
                        updateScrollHints();
                      }, 50);
                    }}
                    className="overflow-x-scroll"
                    style={{ 
                      overscrollBehaviorY: 'auto', 
                      overscrollBehaviorX: 'contain',
                      touchAction: 'pan-x'
                    }}
                    hideScrollbars={false}
                    activationDistance={10}
                  >
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-accent border-b border-border">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-accent transition-colors"
                          onClick={() => handleSort('time')}
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Ώρα
                            {sortField === 'time' && (
                              sortDirection === 'asc' ? 
                              <ChevronUp className="h-4 w-4" /> : 
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-accent transition-colors"
                          onClick={() => handleSort('customer')}
                        >
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Πελάτης
                            {sortField === 'customer' && (
                              sortDirection === 'asc' ? 
                              <ChevronUp className="h-4 w-4" /> : 
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-accent transition-colors"
                          onClick={() => handleSort('party_size')}
                        >
                          <div className="flex items-center gap-2">
                            Άτομα
                            {sortField === 'party_size' && (
                              sortDirection === 'asc' ? 
                              <ChevronUp className="h-4 w-4" /> : 
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                          Τραπέζι
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                          Κατάσταση
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                          Ενέργειες
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                    {getFilteredReservations()
                      .map((reservation) => {
                        const getStatusColor = (status: Reservation["status"]) => {
                          switch (status) {
                            case "confirmed":
                              return "bg-blue-200 text-blue-900 border-blue-400";
                            case "in-progress":
                              return "bg-amber-200 text-amber-900 border-amber-400";
                            case "completed":
                              return "bg-green-200 text-green-900 border-green-400";
                            case "cancelled":
                              return "bg-red-200 text-red-900 border-red-400";
                            case "no-show":
                              return "bg-gray-300 text-gray-900 border-gray-400";
                            default:
                              return "bg-purple-200 text-purple-900 border-purple-400";
                          }
                        };

                        const getStatusIcon = (status: Reservation["status"]) => {
                          switch (status) {
                            case "confirmed":
                              return Calendar;
                            case "in-progress":
                              return UtensilsCrossed;
                            case "completed": 
                              return CheckCircle;
                            case "cancelled":
                              return XCircle;
                            case "no-show":
                              return Ghost;
                            default:
                              return Clock;
                          }
                        };

                        const getStatusText = (status: Reservation["status"]) => {
                          switch (status) {
                            case "confirmed":
                              return "Επιβεβαιωμένη";
                            case "in-progress":
                              return "Σε εξέλιξη";
                            case "completed":
                              return "Ολοκληρωμένη";
                            case "cancelled":
                              return "Ακυρωμένη";
                            case "no-show":
                              return "Δεν εμφανίστηκε";
                            default:
                              return status;
                          }
                        };

                        return (
                          <tr 
                            id={`reservation-row-${reservation.id}`}
                            key={reservation.id} 
                            className="transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
                            onClick={(e) => handleRowClick(reservation, e)}
                          >
                            <td className="px-4 py-4">
                              <div className="font-mono text-sm font-medium text-foreground">
                                {reservation.time}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div>
                                <div className="font-medium text-foreground flex items-center gap-1.5">
                                  {reservation.customer?.name || "Άγνωστος πελάτης"}
                                  {/* Notes indicator */}
                                  {reservationHasUserFacingNotes(reservation) && (
                                    <AlertCircle
                                      className="h-4 w-4 text-orange-500 flex-shrink-0" 
                                      style={{ filter: 'drop-shadow(0 0 3px rgba(249, 115, 22, 0.6))' }}
                                    />
                                  )}
                                </div>
                                <ReservationListPhoneCell customer={reservation.customer} />
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium text-foreground">
                                  {reservation.party_size}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {renderReservationTables(reservation)}
                            </td>
                            <td className="px-4 py-4">
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(reservation.status)}`}>
                                {(() => {
                                  const StatusIcon = getStatusIcon(reservation.status);
                                  return <StatusIcon className="h-4 w-4" strokeWidth={2} />;
                                })()}
                                {getStatusText(reservation.status)}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-end gap-2">
                                {reservation.status === "confirmed" && !reservation.table && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openSeatDialog(reservation); }}
                                    className="w-8 h-8 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-lg flex items-center justify-center transition-colors"
                                    title="Ανάθεση τραπεζιού"
                                  >
                                    <UtensilsCrossed className="h-4 w-4 text-blue-600" />
                                  </button>
                                )}
                                {reservation.status === "confirmed" && reservation.table && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleUnassignTable(reservation); }}
                                    className="w-8 h-8 bg-slate-200 hover:bg-slate-300 border border-slate-300 rounded-lg flex items-center justify-center transition-colors"
                                    title="Αποδέσμευση τραπεζιού"
                                  >
                                    <Undo2 className="h-4 w-4 text-slate-700" />
                                  </button>
                                )}
                                {reservation.status === "confirmed" && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleCheckInReservation(reservation.id); }}
                                    className="w-8 h-8 bg-green-50 hover:bg-green-100 border border-green-300 rounded-lg flex items-center justify-center transition-colors"
                                    title="Check-in"
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </button>
                                )}
                                {["in-progress", "completed", "no-show", "cancelled"].includes(reservation.status) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleReopenReservation(reservation.id); }}
                                    className="w-8 h-8 bg-sky-100 hover:bg-sky-200 border border-sky-400 rounded-lg flex items-center justify-center transition-colors"
                                    title="Επαναφορά"
                                  >
                                    <RotateCcw className="h-4 w-4 text-sky-600" />
                                  </button>
                                )}
                                {shouldShowNoShowButton(reservation) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleMarkNoShow(reservation.id); }}
                                    className="w-8 h-8 bg-gray-200 hover:bg-gray-300 border border-gray-400 rounded-lg flex items-center justify-center transition-colors"
                                    title="Σήμανση ως Δεν εμφανίστηκε"
                                  >
                                    <UserX className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEditReservation(reservation); }}
                                  className="w-8 h-8 bg-blue-200 hover:bg-blue-300 border border-blue-400 rounded-lg flex items-center justify-center transition-colors"
                                  title="Επεξεργασία"
                                >
                                  <Edit className="h-4 w-4 text-blue-600" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteReservation(reservation.id); }}
                                  className="w-8 h-8 bg-red-200 hover:bg-red-300 border border-red-400 rounded-lg flex items-center justify-center transition-colors"
                                  title="Διαγραφή"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Empty State - Desktop */}
                  {getFilteredReservations().length === 0 && (
                    <div className="text-center py-12">
                      <List className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">Δεν υπάρχουν κρατήσεις</h3>
                      <p className="text-muted-foreground">
                        {statusFilter === 'all' 
                          ? "Δεν βρέθηκαν κρατήσεις για την επιλεγμένη ημερομηνία και βάρδια."
                          : "Δεν βρέθηκαν κρατήσεις με την επιλεγμένη κατάσταση."
                        }
                      </p>
                    </div>
                    )}
                  </ScrollContainer>
                  )}
                </div>

                {/* Scroll Hints — desktop only, mobile list has no horizontal scroll */}
                {!isMobile && showListScrollRightHint && getFilteredReservations().length > 0 && (
                  <div className="pointer-events-auto absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background via-background/60 to-transparent flex items-center justify-center z-20">
                    <button
                      type="button"
                      onClick={scrollListToActions}
                      className="rounded-full bg-primary/10 border border-primary/40 p-2 shadow-sm hover:bg-primary/15 active:scale-[0.97] transition"
                      title="Προβολή ενεργειών"
                    >
                      <ChevronRight className="h-5 w-5 text-primary animate-pulse drop-shadow-sm" strokeWidth={2.5} />
                    </button>
              </div>
            )}
                {!isMobile && showListScrollLeftHint && getFilteredReservations().length > 0 && (
                  <div className="pointer-events-auto absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background via-background/60 to-transparent flex items-center justify-center z-20">
                    <button
                      type="button"
                      onClick={scrollListToStart}
                      className="rounded-full bg-primary/10 border border-primary/40 p-2 shadow-sm hover:bg-primary/15 active:scale-[0.97] transition"
                      title="Επιστροφή στην αρχή"
                    >
                      <ChevronLeft className="h-5 w-5 text-primary animate-pulse drop-shadow-sm" strokeWidth={2.5} />
                    </button>
                  </div>
                )}
              </div>

          </div>
        </div>
      )}
      
      <Dialog
        open={!!reservationToSeat}
        onOpenChange={(open) => {
          if (!open) {
            closeSeatDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]" onOpenAutoFocus={(event) => event.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Ανάθεση τραπεζιού</DialogTitle>
            <DialogDescription className="sr-only">
              Επιλέξτε διαθέσιμο τραπέζι για την τρέχουσα κράτηση.
            </DialogDescription>
          </DialogHeader>
          {reservationToSeat && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                <div className="font-semibold text-foreground">
                  {reservationToSeat.customer?.name || 'Άγνωστος πελάτης'}
                </div>
                <div className="text-muted-foreground">
                  {formatDate(new Date(reservationToSeat.date))} • {reservationToSeat.time}
                </div>
                <div className="text-muted-foreground">
                  {reservationToSeat.party_size} άτομα • Βάρδια{' '}
                  {shifts.find(shift => shift.id === reservationToSeat.shift_id)?.name || '—'}
                </div>
              </div>

              {seatOptions.length > 0 ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Επιλέξτε διαθέσιμο τραπέζι
                  </label>
                  <Select value={selectedSeatTableId} onValueChange={setSelectedSeatTableId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Επιλέξτε τραπέζι" />
                    </SelectTrigger>
                    <SelectContent>
                      {seatOptions.map(table => (
                        <SelectItem key={table.id} value={table.id}>
                          Τραπέζι {table.number} • {table.zone} • {table.seats} άτομα
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  Δεν υπάρχουν διαθέσιμα τραπέζια που να καλύπτουν αυτή την κράτηση (διάρκεια 2 ώρες + 5 λεπτά).
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeSeatDialog}>
                  Ακύρωση
                </Button>
                <Button
                  onClick={handleSeatReservationSubmit}
                  disabled={!selectedSeatTableId || seatOptions.length === 0}
                >
                  Ανάθεση
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Reservation Dialog */}
      {editingReservation && (
        <ReservationForm
          customers={customers}
          tables={tables}
          shifts={shifts}
          reservations={reservations}
          onSubmit={handleUpdateReservation}
          isLoading={isLoading}
          editingReservation={editingReservation}
          onCancel={() => setEditingReservation(null)}
        />
      )}
      
      {/* Reservation Details Modal */}
      {detailsReservation && (
        <Dialog open={!!detailsReservation} onOpenChange={(open) => !open && setDetailsReservation(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-semibold text-foreground truncate">
                    {detailsReservation.customer?.name || "Άγνωστος πελάτης"}
                  </div>
                  <div className={cn(
                    "text-sm font-medium inline-block px-2 py-0.5 rounded-full mt-1",
                    detailsReservation.status === "in-progress" && "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
                    detailsReservation.status === "confirmed" && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                    detailsReservation.status === "completed" && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                    detailsReservation.status === "cancelled" && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                    detailsReservation.status === "no-show" && "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
                  )}>
                    {detailsReservation.status === "in-progress" && "Σε εξέλιξη"}
                    {detailsReservation.status === "confirmed" && "Επιβεβαιωμένη"}
                    {detailsReservation.status === "completed" && "Ολοκληρωμένη"}
                    {detailsReservation.status === "cancelled" && "Ακυρωμένη"}
                    {detailsReservation.status === "no-show" && "Δεν εμφανίστηκε"}
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Contact — tel: link + clear affordance */}
              {(() => {
                const phoneDisplay = getCustomerPhoneForDisplay(detailsReservation.customer);
                const telHref = phoneDisplay ? getTelHrefForPhone(phoneDisplay) : null;
                if (!phoneDisplay) return null;
                return (
                  <div className="rounded-xl border border-border/80 bg-muted/35 p-3.5 space-y-1.5">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Τηλέφωνο
                    </div>
                    {telHref ? (
                      <a
                        href={telHref}
                        className="inline-flex items-center gap-2 text-base font-semibold text-primary underline-offset-2 hover:underline decoration-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md -m-0.5 p-0.5"
                        aria-label={`Κλήση στο ${phoneDisplay}`}
                      >
                        <Phone className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        <span className="tabular-nums">{phoneDisplay}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="tabular-nums">{phoneDisplay}</span>
                      </div>
                    )}
                    {telHref && (
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Πατήστε για κλήση από τη συσκευή σας
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Reservation Details */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Ώρα</div>
                  <div className="font-medium text-foreground flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {detailsReservation.time}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Άτομα</div>
                  <div className="font-medium text-foreground flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {detailsReservation.party_size}
                  </div>
                </div>
              </div>

              {/* Table */}
              {detailsReservation.table && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Τραπέζι</div>
                  <div className="font-medium text-foreground">
                    Τραπέζι {detailsReservation.table.number} ({detailsReservation.table.zone})
                  </div>
                </div>
              )}

              {/* Source */}
              {detailsReservation.source && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Πηγή</div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground">
                    {(() => {
                      const sourceIcons: Record<string, React.ElementType> = {
                        online: Globe,
                        manual: UtensilsCrossed,
                        phone: Phone,
                        'walk-in': UserPlus,
                        unknown: UtensilsCrossed,
                      };
                      const sourceLabels: Record<string, string> = {
                        online: 'Online',
                        manual: 'Εσωτερικό',
                        phone: 'Τηλέφωνο',
                        'walk-in': 'Walk-in',
                        unknown: 'Άγνωστο',
                      };
                      const SourceIcon = sourceIcons[detailsReservation.source || 'unknown'];
                      return (
                        <>
                          <SourceIcon className="h-4 w-4" />
                          <span>{sourceLabels[detailsReservation.source || 'unknown']}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Reservation Notes — hide system walk-in placeholder */}
              {getReservationNotesForDisplay(detailsReservation.notes) && (
                <div className="rounded-lg bg-muted p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <span>Σημειώσεις Κράτησης</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getReservationNotesForDisplay(detailsReservation.notes)}
                  </p>
                </div>
              )}

              {/* Customer Notes — hide system walk-in customer text */}
              {getCustomerNotesForDisplay(detailsReservation.customer?.notes) && (
                <div className="rounded-lg bg-accent p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-accent-foreground">
                    <User className="h-4 w-4" />
                    <span>Σημειώσεις Πελάτη</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getCustomerNotesForDisplay(detailsReservation.customer?.notes)}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-2 border-t">
              {detailsReservation.status === "confirmed" && !detailsReservation.table && (
                <Button
                  onClick={() => {
                    openSeatDialog(detailsReservation);
                    setDetailsReservation(null);
                  }}
                  variant="outline"
                  className="w-full border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                >
                  <UtensilsCrossed className="h-4 w-4 mr-2" />
                  Ανάθεση τραπεζιού
                </Button>
              )}
              {detailsReservation.status === "confirmed" && (
                <Button
                  onClick={() => {
                    handleCheckInReservation(detailsReservation.id);
                    setDetailsReservation(null);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Check-in
                </Button>
              )}
              {detailsReservation.status === "in-progress" && (
                <Button
                  onClick={() => {
                    handleCheckoutReservation(detailsReservation.id);
                    setDetailsReservation(null);
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Ολοκληρώθηκε
                </Button>
              )}
              {["in-progress", "completed", "no-show", "cancelled"].includes(detailsReservation.status) && (
                <Button
                  onClick={() => {
                    handleReopenReservation(detailsReservation.id);
                    setDetailsReservation(null);
                  }}
                  variant="outline"
                  className="w-full border-sky-400 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Επαναφορά
                </Button>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setEditingReservation(detailsReservation);
                    setDetailsReservation(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Επεξεργασία
                </Button>
                {!["cancelled", "completed", "no-show"].includes(detailsReservation.status) && (
                  <Button
                    onClick={() => {
                      setShowDeleteConfirm(detailsReservation.id);
                      setDetailsReservation(null);
                    }}
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Διαγραφή
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Επιβεβαίωση Διαγραφής</AlertDialogTitle>
            <AlertDialogDescription>
              Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την κράτηση; 
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteReservation}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reservations;
